import { useEffect, useRef, useState } from 'react';
import { call, saveToken, announceSignedIn } from './api.js';
import { go } from './nav.js';
import { returnParam, withReturn } from './gate.jsx';
import { useChannels } from './phone.jsx';
import { visitLanguage } from './voice.js';
import { readCentre, whereCity, restoreHome } from './place.js';

/* ══════════════════════════════════════════════════════════════════════════
 * SIGN IN WITH GOOGLE — Al-Noor, 28 Oct 2026 ("Yes, build a Google sign-in").
 *
 * Google's OWN button (Google Identity Services) — its branding rules ask for
 * it, and its words are the locked ones: "Sign up with Google" on Welcome
 * (02_Welcome.html), "Sign in with Google" on Sign in. A new Eleater then
 * finishes on 03b ("Google confirmed you") inside Join.jsx (`by=google`).
 *
 * ⚠️ OFF UNTIL eleat-api HAS `GOOGLE_CLIENT_ID` — /auth/channels hands the
 * (public) client id over; with none, nothing is drawn.
 * ══════════════════════════════════════════════════════════════════════════ */
const GSI = 'https://accounts.google.com/gsi/client';
let loading = null;
function loadGsi() {
  if (window.google && window.google.accounts && window.google.accounts.id) return Promise.resolve();
  loading = loading || new Promise((ok, no) => {
    const s = document.createElement('script');
    s.src = GSI; s.async = true; s.defer = true;
    s.onload = () => ok(); s.onerror = () => { loading = null; no(new Error('Google did not load')); };
    document.head.appendChild(s);
  });
  return loading;
}

const BILLING = ['CA', 'US', 'HK', 'CN'];
function country() {
  const c = readCentre();
  const iso = c && c.country_code ? String(c.country_code).toUpperCase() : null;
  if (!iso) return undefined;
  return BILLING.includes(iso) ? iso : 'OTHER';
}

export const GOOGLE_KEY = 'eleat.google';

/* Google's own four-colour "G" — unmodified, as Google's branding rules ask */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ flex: 'none' }} aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/** mode: 'signup' (Welcome) or 'signin' (Sign in).
    ⚠️ ELEAT'S OWN BUTTON — Al-Noor, 30 Oct 2026: "It has to look uniform" with
    "Join with email": the same size, border and colour (`.authbtn`, the
    Welcome pill's own values). Google's drawn button cannot be restyled, so
    this opens Google's sign-in window itself (GIS token client) and the server
    checks the token with Google. The Apple button, when it comes, is this
    same shape. */
export function GoogleButton({ mode }) {
  const { google_client_id: id } = useChannels();
  const client = useRef(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function arrive(accessToken) {
    try {
      const lang = visitLanguage();
      const d = await call('/auth/google', {
        method: 'POST',
        body: { access_token: accessToken, market: country(),
          preferred_language: lang || undefined, city: whereCity() || undefined },
      });
      if (!d || !d.token) throw new Error('Eleat did not send a sign-in back. Try again.');
      saveToken(d.token, true);
      if (d.needs_finish) {
        try { sessionStorage.setItem(GOOGLE_KEY, JSON.stringify({
          email: d.email, first_name: d.first_name, last_name: d.last_name })); } catch { /* fine */ }
        go(withReturn('?join=1&by=google', returnParam()), { replace: true });
        return;
      }
      await restoreHome();
      announceSignedIn();
      go(returnParam(), { replace: true });
    } catch (e) { setError(e.message || 'Google sign-in did not work. Try again.'); }
    finally { setBusy(false); }
  }

  /* Google's script is fetched ahead, so the tap opens the window at once —
     a window opened after a wait is blocked by the browser as a pop-up */
  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    loadGsi().then(() => {
      if (!live) return;
      client.current = window.google.accounts.oauth2.initTokenClient({
        client_id: id,
        scope: 'openid email profile',
        callback: (resp) => {
          if (resp && resp.access_token) arrive(resp.access_token);
          else { setBusy(false); if (resp && resp.error) setError('Google sign-in did not work. Try again.'); }
        },
        error_callback: () => setBusy(false),
      });
    }).catch(() => { if (live) setError('Google sign-in did not load. Check your connection.'); });
    return () => { live = false; };
  }, [id]);

  if (!id) return null;
  return (
    <div className="gbtn">
      <button type="button" className="authbtn" disabled={busy}
        onClick={() => {
          setError('');
          if (!client.current) { setError('Google sign-in is still loading. Try again in a moment.'); return; }
          setBusy(true);
          client.current.requestAccessToken();
        }}>
        <GoogleG />
        <span>{mode === 'signin' ? 'Sign in with Google' : 'Sign up with Google'}</span>
      </button>
      {error && <div className="err" aria-live="polite">{error}</div>}
    </div>
  );
}
